import { useEffect, useState } from 'react';
import {
  Mail, Send, Plus, Trash2, AlertCircle, CheckCircle2, Users, Megaphone, RefreshCw,
} from 'lucide-react';
import Card from '../../components/Card';
import Button from '../../components/Button';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useConfirm } from '../../components/ConfirmDialog';
import { customerPortalApi, type Campaign, type Subscriber } from '../../api/customerPortal';
import { formatDate } from '../../utils/formatTime';

const STATUS_TONE: Record<string, string> = {
  sent: 'bg-green-500/10 text-green-600 dark:text-green-400',
  draft: 'bg-slate-500/10 text-slate-600 dark:text-slate-400',
  sending: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
  failed: 'bg-red-500/10 text-red-600 dark:text-red-400',
};

export default function NewsletterApp() {
  const confirm = useConfirm();
  const [subs, setSubs] = useState<Subscriber[]>([]);
  const [subCount, setSubCount] = useState(0);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [audience, setAudience] = useState({ subscribers: 0, customers: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [busy, setBusy] = useState(false);

  const [newEmail, setNewEmail] = useState('');
  const [form, setForm] = useState({ name: '', subject: '', body: '', kind: 'newsletter', audience: 'subscribers' });

  const load = async () => {
    setLoading(true); setError('');
    const results = await Promise.allSettled([
      customerPortalApi.listSubscribers(),
      customerPortalApi.listCampaigns(),
    ]);
    const [subRes, campRes] = results;
    const failures: string[] = [];
    if (subRes.status === 'fulfilled') { setSubs(subRes.value.subscribers); setSubCount(subRes.value.subscribed_count); }
    else failures.push('subscribers');
    if (campRes.status === 'fulfilled') { setCampaigns(campRes.value.campaigns); setAudience(campRes.value.audience_counts); }
    else failures.push('campaigns');
    setError(failures.length ? `Could not load: ${failures.join(', ')}.` : '');
    setLoading(false);
  };
  useEffect(() => { void load(); }, []);

  const addSub = async () => {
    if (!newEmail.trim()) return;
    setBusy(true); setError(''); setSuccess('');
    try {
      await customerPortalApi.addSubscriber(newEmail.trim());
      setNewEmail(''); setSuccess('Subscriber added.');
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to add subscriber');
    } finally { setBusy(false); }
  };

  const createCampaign = async () => {
    if (!form.name.trim() || !form.subject.trim()) { setError('Name and subject are required.'); return; }
    setBusy(true); setError(''); setSuccess('');
    try {
      await customerPortalApi.createCampaign({ ...form });
      setForm({ name: '', subject: '', body: '', kind: 'newsletter', audience: 'subscribers' });
      setSuccess('Campaign saved as draft.');
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to create campaign');
    } finally { setBusy(false); }
  };

  const sendCampaign = async (c: Campaign) => {
    const ok = await confirm({
      title: `Send "${c.name}"?`,
      message: `This will attempt delivery to the "${c.audience}" audience. Sent campaigns cannot be edited.`,
      actions: [
        { label: 'Cancel', value: false, tone: 'secondary' },
        { label: 'Send', value: 'send', tone: 'primary' },
      ],
    });
    if (ok === false) return;
    setBusy(true); setError(''); setSuccess('');
    try {
      const res = await customerPortalApi.sendCampaign(c.id);
      if (res.status === 'failed') setError(res.detail || 'Send failed.');
      else setSuccess(`Campaign sent: ${res.sent_count} delivered, ${res.failed_count} failed.`);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to send campaign');
    } finally { setBusy(false); }
  };

  const removeCampaign = async (c: Campaign) => {
    const ok = await confirm({
      title: 'Delete campaign?',
      message: `"${c.name}" will be permanently removed.`,
      actions: [
        { label: 'Cancel', value: false, tone: 'secondary' },
        { label: 'Delete', value: 'delete', tone: 'danger' },
      ],
    });
    if (ok === false) return;
    try {
      await customerPortalApi.deleteCampaign(c.id);
      setSuccess('Campaign deleted.');
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to delete campaign');
    }
  };

  if (loading) return <div className="flex h-40 items-center justify-center"><LoadingSpinner size="lg" /></div>;

  return (
    <div className="space-y-6">
      {error && <div className="flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2.5 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-400"><AlertCircle size={16} />{error}</div>}
      {success && <div className="flex items-center gap-2 rounded-lg border border-green-300 bg-green-50 px-3 py-2.5 text-sm text-green-700 dark:border-green-800 dark:bg-green-900/30 dark:text-green-400"><CheckCircle2 size={16} />{success}</div>}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
        <Card className="p-4"><div className="text-2xl font-bold text-slate-950 dark:text-slate-100">{subCount}</div><div className="text-xs text-slate-500">Subscribed</div></Card>
        <Card className="p-4"><div className="text-2xl font-bold text-slate-950 dark:text-slate-100">{audience.customers}</div><div className="text-xs text-slate-500">Customers</div></Card>
        <Card className="p-4"><div className="text-2xl font-bold text-slate-950 dark:text-slate-100">{campaigns.length}</div><div className="text-xs text-slate-500">Campaigns</div></Card>
      </div>

      <Card className="p-5">
        <div className="mb-3 flex items-center gap-2"><Megaphone size={18} className="text-slate-500" /><h3 className="text-base font-semibold text-slate-950 dark:text-slate-100">New Campaign</h3></div>
        <div className="grid gap-3 sm:grid-cols-2">
          <input placeholder="Internal name" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100" />
          <input placeholder="Subject line" value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100" />
          <select value={form.kind} onChange={e => setForm(p => ({ ...p, kind: e.target.value }))}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100">
            <option value="newsletter">Newsletter</option>
            <option value="promo">Promotion</option>
            <option value="notice">Notice</option>
          </select>
          <select value={form.audience} onChange={e => setForm(p => ({ ...p, audience: e.target.value }))}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100">
            <option value="subscribers">Newsletter subscribers ({audience.subscribers})</option>
            <option value="customers">Customers ({audience.customers})</option>
          </select>
        </div>
        <textarea rows={3} placeholder="Body (HTML or text)" value={form.body} onChange={e => setForm(p => ({ ...p, body: e.target.value }))}
          className="mt-3 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100" />
        <div className="mt-3">
          <Button size="sm" onClick={createCampaign} disabled={busy}><Plus size={14} />Save draft</Button>
        </div>
      </Card>

      <Card className="p-0">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3 dark:border-slate-800">
          <h3 className="text-sm font-semibold text-slate-950 dark:text-slate-100">Campaigns</h3>
          <Button size="sm" variant="secondary" onClick={() => load()} disabled={loading}><RefreshCw size={14} /></Button>
        </div>
        {campaigns.length === 0 ? (
          <p className="px-5 py-6 text-sm text-slate-500">No campaigns yet.</p>
        ) : (
          <div className="divide-y divide-slate-100 dark:divide-slate-800">
            {campaigns.map(c => (
              <div key={c.id} className="flex flex-col gap-2 px-5 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-slate-950 dark:text-slate-100">{c.name}</span>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_TONE[c.status] || 'bg-slate-500/10 text-slate-500'}`}>{c.status}</span>
                    <span className="text-xs capitalize text-slate-400">{c.kind} · {c.audience}</span>
                  </div>
                  <div className="truncate text-xs text-slate-500">{c.subject}</div>
                  {c.detail && <div className="text-xs text-slate-400">{c.detail}</div>}
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  <span className="text-xs text-slate-500">{c.sent_count}/{c.recipient_count} sent</span>
                  {c.status !== 'sent' && c.status !== 'sending' && (
                    <button onClick={() => sendCampaign(c)} disabled={busy} className="inline-flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-500"><Send size={12} />Send</button>
                  )}
                  <button onClick={() => removeCampaign(c)} className="text-slate-400 hover:text-red-500" aria-label={`Delete ${c.name}`}><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="p-5">
        <div className="mb-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2"><Users size={18} className="text-slate-500" /><h3 className="text-base font-semibold text-slate-950 dark:text-slate-100">Subscribers</h3></div>
        </div>
        <div className="mb-4 flex gap-2">
          <input placeholder="email@example.com" value={newEmail} onChange={e => setNewEmail(e.target.value)}
            className="flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100" />
          <Button size="sm" onClick={addSub} disabled={busy}><Plus size={14} />Add</Button>
        </div>
        {subs.length === 0 ? <p className="text-sm text-slate-500">No subscribers yet.</p> : (
          <div className="max-h-80 overflow-y-auto">
            {subs.map(s => (
              <div key={s.id} className="flex items-center justify-between border-b border-slate-100 py-2 last:border-0 dark:border-slate-800">
                <span className="min-w-0">
                  <span className="block truncate text-sm text-slate-700 dark:text-slate-300">{s.email}</span>
                  <span className="block text-xs text-slate-400">{s.source || 'manual'}{s.created_at ? ` · ${formatDate(s.created_at)}` : ''}</span>
                </span>
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${s.status === 'subscribed' ? 'bg-green-500/10 text-green-600 dark:text-green-400' : 'bg-slate-500/10 text-slate-500'}`}>{s.status}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
